import type {
  PackagesAutocompleteResult,
  PackageType,
} from "@b2bportal/core-packages";
import { CallState } from "@b2bportal/core-types";
import type { DateRange, Travelers } from "@b2bportal/packages-api";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { fetchPackageSearchParams } from "../thunks/general";
import { initializePackages } from "../thunks/initializePackages";
import type {
  PackagesSearch,
  NewPackagesSearch,
  PackageSearchState,
} from "../types/packagesSearch";
import { initialSearchState } from "../types/packagesSearch";

// Re-export types for backwards compatibility
export type { PackagesSearch, NewPackagesSearch, PackageSearchState };

export const packagesSearchSlice = createSlice({
  name: "packages/search",
  initialState: initialSearchState,
  reducers: {
    setNewGuests: (state, action: PayloadAction<Travelers>) => {
      state.searchFormValues.guests = action.payload;
    },
    setNewDestination: (
      state,
      action: PayloadAction<PackagesAutocompleteResult>
    ) => {
      state.searchFormValues.destination = action.payload;
    },
    setNewOrigin: (
      state,
      action: PayloadAction<PackagesAutocompleteResult>
    ) => {
      state.searchFormValues.origin = action.payload;
    },
    setNewDateFrom: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dateRange = {
        ...state.searchFormValues.dateRange,
        from: action.payload,
      };
    },
    setNewDateUntil: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dateRange = {
        ...state.searchFormValues.dateRange,
        until: action.payload,
      };
    },
    setNewDateRange: (state, action: PayloadAction<DateRange>) => {
      state.searchFormValues.dateRange = action.payload;
    },
    setRequestedSearch: (state, action: PayloadAction<PackagesSearch>) => {
      state.searchFormValues = action.payload;
      state.requestedSearch = action.payload;
    },
    setSearch: (state, action: PayloadAction<PackagesSearch>) => {
      state.searchFormValues = action.payload;
      state.requestedSearch = action.payload;
      state.search = action.payload;
    },
    setNewPackageType: (state, action: PayloadAction<PackageType>) => {
      state.searchFormValues.packageType = action.payload;
    },
    setDriverAge: (state, action: PayloadAction<number | undefined>) => {
      state.searchFormValues.driverAge = action.payload;
    },
    // Called before navigating into the upsell. Clears stale origin/destination
    // so the auto-search returns early on re-entry and isSameSearch = false
    // triggers a fresh initializePackages for the new flight context.
    resetPackagesSearch: (state) => {
      state.searchFormValues = {
        ...initialSearchState.searchFormValues,
        // Preserve packageType so the correct funnel is maintained
        packageType: state.searchFormValues.packageType,
      };
      state.requestedSearch = undefined;
      state.initialized = undefined;
      state.isInitializingPackages = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializePackages.pending, (state) => {
        state.initialized = CallState.InProcess;
        state.isInitializingPackages = true;
      })
      .addCase(
        initializePackages.fulfilled,
        (state, action: PayloadAction<PackageSearchState>) => {
          state.initialized = CallState.Success;
          state.isInitializingPackages = false;
          state.requestedSearch = action.payload.requestedSearch;
          state.searchFormValues = action.payload.searchFormValues;
        }
      )
      .addCase(initializePackages.rejected, (state, action) => {
        // An abort only comes from a newer initializePackages, whose .pending
        // has already run. Flipping to Failed here would clear the InProcess
        // guards while the newer search is still in flight.
        if (action.meta.aborted) {
          return;
        }
        state.initialized = CallState.Failed;
        state.isInitializingPackages = false;
      })
      // fetchPackageSearchParams is dispatched both standalone (flight/car/shop
      // pages) and nested inside initializePackages. Standalone callers need the
      // full InProcess → Success/Failed lifecycle. But when nested, flipping
      // initialized to Success before initializePackages.fulfilled fires creates
      // a false isOrphanedUpsell window and lets the auto-search run with stale
      // Hotels dateRange, overwriting the correct URL and looping requisitions.
      //
      // isInitializingPackages tracks ownership: only initializePackages lifecycle
      // actions set/clear it, so the flag reliably distinguishes "outer init is
      // running" from "this fetch's own .pending just ran".
      .addCase(fetchPackageSearchParams.pending, (state) => {
        state.initialized = CallState.InProcess;
      })
      .addCase(
        fetchPackageSearchParams.fulfilled,
        (state, action: PayloadAction<NewPackagesSearch>) => {
          state.searchFormValues = action.payload;
          if (!state.isInitializingPackages) {
            state.initialized = CallState.Success;
          }
        }
      )
      .addCase(fetchPackageSearchParams.rejected, (state) => {
        if (!state.isInitializingPackages) {
          state.initialized = CallState.Failed;
        }
      });
  },
});

export const PackagesSearchActions = packagesSearchSlice.actions;
export default packagesSearchSlice.reducer;
